import React, { useMemo } from 'react';
import {
    Alert,
    Box,
    Chip,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import { dateTimeApiToUi } from '../../const/dates';

export const REPROGRAMAR_MENSAJES = {
    cupo_agotado: 'Cupo de reprogramaciones agotado.',
    antelacion_insuficiente: 'Antelación insuficiente.',
    intervalo_activo: 'Intervalo mínimo entre reprogramaciones activo.',
    turno_vencido: 'El turno vigente ya ocurrió.',
};

/**
 * Criterios visuales de reprogramación (chips + barra de cupo).
 * @param {object} props
 * @param {object} props.ordenOrPractica — orden (con count) o práctica (solo límites)
 * @param {string[]} [props.bloqueos]
 * @param {boolean} [props.readOnly]
 */
export default function ReprogramacionCriterios({
    ordenOrPractica,
    bloqueos = [],
    readOnly = false,
}) {
    const max = Number(ordenOrPractica?.reprogramaciones_max ?? 2);
    const count = Number(ordenOrPractica?.reprogramaciones_count ?? 0);
    const antelacion = Number(ordenOrPractica?.reprogramacion_antelacion_horas ?? 24);
    const cooldown = Number(ordenOrPractica?.reprogramacion_cooldown_horas ?? 12);
    const reprogramadoAt = ordenOrPractica?.reprogramado_at;

    const hasCupo = bloqueos.includes('cupo_agotado');
    const hasAntelacion = bloqueos.includes('antelacion_insuficiente');
    const hasIntervalo = bloqueos.includes('intervalo_activo');
    const hasVencido = bloqueos.includes('turno_vencido');
    const blocked = bloqueos.length > 0;

    const cupoColor = useMemo(() => {
        if (readOnly) return 'info';
        if (hasCupo || max === 0) return 'error';
        if (max - count === 1) return 'warning';
        return 'success';
    }, [readOnly, hasCupo, max, count]);

    const antelacionColor = useMemo(() => {
        if (readOnly) return 'info';
        if (hasAntelacion) return 'error';
        return 'success';
    }, [readOnly, hasAntelacion]);

    const cooldownColor = useMemo(() => {
        if (readOnly) return 'info';
        if (hasIntervalo) return 'error';
        return 'success';
    }, [readOnly, hasIntervalo]);

    const progress = max > 0 ? Math.min(100, (count / max) * 100) : 100;

    const disponibleDesde = useMemo(() => {
        if (!reprogramadoAt || cooldown <= 0) return null;
        const d = new Date(reprogramadoAt);
        d.setHours(d.getHours() + cooldown);
        return d;
    }, [reprogramadoAt, cooldown]);

    const guia = useMemo(() => {
        if (readOnly) {
            return 'Los límites aplican a cada orden creada con esta práctica.';
        }
        if (hasVencido) return REPROGRAMAR_MENSAJES.turno_vencido;
        if (hasCupo) return REPROGRAMAR_MENSAJES.cupo_agotado;
        if (hasAntelacion) return REPROGRAMAR_MENSAJES.antelacion_insuficiente;
        if (hasIntervalo) return REPROGRAMAR_MENSAJES.intervalo_activo;
        if (max - count === 1) return 'Última reprogramación disponible.';
        return 'Reprogramación disponible según los límites de la orden.';
    }, [readOnly, hasVencido, hasCupo, hasAntelacion, hasIntervalo, max, count]);

    return (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
                <Chip
                    size="small"
                    icon={hasCupo || max === 0 ? <BlockIcon /> : <ReplayIcon />}
                    color={cupoColor}
                    variant={cupoColor === 'error' ? 'filled' : 'outlined'}
                    label={`Reprogramaciones: ${count} de ${max}`}
                />
                <Chip
                    size="small"
                    icon={<ScheduleIcon />}
                    color={antelacionColor}
                    variant={antelacionColor === 'error' ? 'filled' : 'outlined'}
                    label={
                        readOnly || antelacion === 0
                            ? (antelacion === 0 ? 'Sin antelación mínima' : `Antelación: ${antelacion} h`)
                            : (hasAntelacion ? 'Antelación insuficiente' : 'Antelación cumplida')
                    }
                />
                <Chip
                    size="small"
                    icon={<HourglassBottomIcon />}
                    color={cooldownColor}
                    variant={cooldownColor === 'error' ? 'filled' : 'outlined'}
                    label={
                        readOnly || cooldown === 0
                            ? (cooldown === 0 ? 'Sin intervalo mínimo' : `Intervalo: ${cooldown} h`)
                            : (hasIntervalo
                                ? (disponibleDesde ? `Disponible desde ${dateTimeApiToUi(disponibleDesde.toISOString())}` : 'Intervalo activo')
                                : 'Intervalo cumplido')
                    }
                />
            </Stack>

            {!readOnly && max > 0 ? (
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={cupoColor === 'default' ? 'primary' : cupoColor}
                    sx={{ height: 6, borderRadius: 1, mb: 1 }}
                />
            ) : null}

            <Typography variant="caption" color="text.secondary" display="block">
                {guia}
            </Typography>

            {!readOnly && blocked ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {bloqueos.map((b) => REPROGRAMAR_MENSAJES[b] || b).join(' ')}
                </Alert>
            ) : null}
        </Box>
    );
}
